// src/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  GoogleAuthProvider,
  FacebookAuthProvider,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Reuse the app if it’s already initialized (prevents hot-reload duplicates)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);

// Robust persistence: prefer IndexedDB → localStorage → memory (never throw)
(async () => {
  try {
    await setPersistence(auth, indexedDBLocalPersistence);
    if (process.env.NODE_ENV !== "production") {
      console.log("[auth] persistence = indexedDBLocalPersistence");
    }
  } catch (e1) {
    try {
      await setPersistence(auth, browserLocalPersistence);
      if (process.env.NODE_ENV !== "production") {
        console.log("[auth] persistence = browserLocalPersistence (fallback)");
      }
    } catch (e2) {
      await setPersistence(auth, inMemoryPersistence);
      if (process.env.NODE_ENV !== "production") {
        console.log("[auth] persistence = inMemoryPersistence (last resort)");
      }
    }
  }
})();

// Export a one-shot “auth is hydrated” promise to avoid sign-in flicker
let _resolveAuthReady;
const authReady = new Promise((resolve) => {
  _resolveAuthReady = resolve;
});

// Resolve after the FIRST auth state event across this tab
let _firstEventSeen = false;
onAuthStateChanged(auth, () => {
  if (!_firstEventSeen) {
    _firstEventSeen = true;
    _resolveAuthReady?.();
  }
  // Note: App.js still registers its own onAuthStateChanged to get the user
});

// Providers
const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();

export { app, auth, authReady, googleProvider, facebookProvider };
